import React, { useMemo, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ReactComponent as ICHLogo } from "../../assets/icons/ichLogo.svg";
import { ReactComponent as ICHNextLogo } from "../../assets/icons/ichNextLogo.svg";
import { ReactComponent as ArrowIcon } from "../../assets/icons/arrow.svg";
import GenerativeIcon from "../../assets/icons/GenerativeIcon";
import TextGeneratorIcon from "../../assets/icons/textGeneratorIcon";
import ContentIcon from "../../assets/icons/ContentIcon";
import CollapseIcon from "../../assets/icons/CollapseIcon";
import BoardsIcon from "../../assets/icons/BoardsIcon";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import {
  CLIENT_SSO_BASE_PATH,
  CLIENT_SSO_COLLECTION_BUILD_PATH,
  CLIENT_SSO_IMAGE_GENERATOR_PATH,
  CLIENT_SSO_LIBRARY_PATH,
} from "../../constants/clientSso";
import ImageGeneratorRecents, {
  buildGenerativeRecentItems,
} from "../../pages/GenerativePlatform/ImageGeneratorRecents";
import CollectionBuildRecents, {
  buildCollectionRecentItems,
} from "../../pages/GenerativePlatform/CollectionBuildRecents";
import { useImageGeneratorJobsOptional } from "../../pages/GenerativePlatform/ImageGeneratorJobsContext";
import { useCollectionBuildSessionsOptional } from "../../pages/GenerativePlatform/CollectionBuildSessionsContext";
import {
  IMAGE_GENERATOR_SESSION_PARAM,
  imageGeneratorRootId,
} from "../../pages/GenerativePlatform/imageGeneratorSession";
import { readCollectionBuildSessionParam } from "../../pages/GenerativePlatform/collectionBuildSession";

const ClientSsoSidebar = ({
  isCollapsed,
  onToggle,
}: {
  isCollapsed: boolean;
  onToggle: () => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const imageJobs = useImageGeneratorJobsOptional();
  const collectionJobs = useCollectionBuildSessionsOptional();
  const [isGenerativeFeatureOpen, setGenerativeFeatureOpen] = useState(true);
  const [isRecentsOpen, setRecentsOpen] = useState(true);

  // HOME_PATH aliases Image Generator — never use it to detect Collection Build.
  const onCollectionPage = location.pathname.startsWith(
    CLIENT_SSO_COLLECTION_BUILD_PATH
  );
  const onImageGeneratorPage = location.pathname.startsWith(
    CLIENT_SSO_IMAGE_GENERATOR_PATH
  );
  const collectionSessionId = readCollectionBuildSessionParam(searchParams);
  const imageSessionId = Number(
    searchParams.get(IMAGE_GENERATOR_SESSION_PARAM) || ""
  );
  const recentsItems = useMemo(
    () =>
      onCollectionPage
        ? buildCollectionRecentItems(collectionJobs?.summaries || [])
        : onImageGeneratorPage
          ? buildGenerativeRecentItems(imageJobs?.sessions || [])
          : [],
    [
      collectionJobs?.summaries,
      imageJobs?.sessions,
      onCollectionPage,
      onImageGeneratorPage,
    ]
  );
  const recentsActiveKey = useMemo(() => {
    if (onCollectionPage) {
      return collectionSessionId ? `collection-${collectionSessionId}` : null;
    }
    if (!onImageGeneratorPage) {
      return null;
    }
    if (!Number.isInteger(imageSessionId) || imageSessionId < 1) {
      return null;
    }
    const sessions = imageJobs?.sessions || [];
    const byId = new Map(sessions.map(item => [item.id, item]));
    const opened = byId.get(imageSessionId);
    const rootId = opened ? imageGeneratorRootId(opened, byId) : imageSessionId;
    return `image-${rootId}`;
  }, [
    collectionSessionId,
    imageJobs?.sessions,
    imageSessionId,
    onCollectionPage,
    onImageGeneratorPage,
  ]);

  const isParentActive = (parentPath: string) =>
    location.pathname.startsWith(parentPath)
      ? "bg-primary text-white font-normal"
      : "text-secondary hover:bg-gray";

  const isChildActive = (childPath: string) =>
    location.pathname === childPath
      ? "bg-active text-black font-normal"
      : "text-secondary hover:bg-gray";

  const getSvgColor = (parentPath: string) =>
    location.pathname.startsWith(parentPath) ? "#ffffff" : "#353E43";

  const getParentSvgClass = (parentPath: string) =>
    location.pathname.startsWith(parentPath) ? "text-white" : "text-tertiary";

  const getSvgColorNews = (childPath: string) =>
    location.pathname === childPath ? "#000000" : "#4d4d4d";

  const getChildSvgClass = (childPath: string) =>
    location.pathname === childPath ? "text-black" : "text-tertiary";

  const getArrowClass = (isOpen: boolean, parentPath: string) =>
    `${isOpen ? "rotate-180" : ""} ${
      location.pathname.startsWith(parentPath) ? "text-white" : "text-black"
    }`;

  return (
    <div
      className={`h-screen bg-white transition-all px-3 duration-500 ease-in-out ${
        isCollapsed ? "w-24" : "w-64"
      }`}
      style={{
        userSelect: "none",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        overflow: "hidden",
      }}
    >
      <div>
        <div className="p-2 mt-6 flex justify-center">
          {isCollapsed ? <ICHLogo /> : <ICHNextLogo />}
        </div>
        <nav
          className={`mt-4 ${isCollapsed ? "px-2" : ""} max-h-[80vh] scroller`}
          style={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}
        >
          <ul>
            <li>
              <Link
                to={CLIENT_SSO_BASE_PATH}
                className={`flex items-center py-4 px-2 cursor-pointer rounded-2xl mb-2 ${isParentActive(
                  CLIENT_SSO_BASE_PATH
                )}`}
                onClick={() => setGenerativeFeatureOpen(open => !open)}
              >
                {isCollapsed ? (
                  <Tippy content="Generative Feature" placement="right">
                    <div
                      className={`flex justify-center items-center w-12 ${getParentSvgClass(
                        CLIENT_SSO_BASE_PATH
                      )}`}
                    >
                      <GenerativeIcon
                        fill={getSvgColor(CLIENT_SSO_BASE_PATH)}
                      />
                    </div>
                  </Tippy>
                ) : (
                  <div
                    className={`flex justify-center items-center w-12 mr-1 ${getParentSvgClass(
                      CLIENT_SSO_BASE_PATH
                    )}`}
                  >
                    <GenerativeIcon fill={getSvgColor(CLIENT_SSO_BASE_PATH)} />
                  </div>
                )}
                <span
                  className={`transition-all duration-500 ease-in-out ${
                    isCollapsed ? "opacity-0 max-w-0" : "opacity-100 max-w-full"
                  }`}
                  style={{ whiteSpace: "nowrap", overflow: "hidden" }}
                >
                  Generative Feature
                </span>
                <ArrowIcon
                  className={`ml-auto transform transition-transform duration-500 ease-in-out ${
                    isCollapsed ? "hidden" : "block"
                  } ${getArrowClass(isGenerativeFeatureOpen, CLIENT_SSO_BASE_PATH)}`}
                />
              </Link>
            </li>
            <div
              className={`transition-max-height duration-500 ease-in-out ${
                isGenerativeFeatureOpen ? "max-h-[70vh]" : "max-h-0"
              } overflow-hidden`}
            >
              <ul className="mb-4">
                <li>
                  <Link
                    to={CLIENT_SSO_IMAGE_GENERATOR_PATH}
                    className={`flex items-center px-2 py-2 rounded-xl mb-2 ${isChildActive(
                      CLIENT_SSO_IMAGE_GENERATOR_PATH
                    )}`}
                  >
                    {isCollapsed ? (
                      <Tippy content="Image Generator" placement="right">
                        <div
                          className={`flex justify-center items-center w-12 ${getChildSvgClass(
                            CLIENT_SSO_IMAGE_GENERATOR_PATH
                          )}`}
                        >
                          <ContentIcon
                            fill={getSvgColorNews(
                              CLIENT_SSO_IMAGE_GENERATOR_PATH
                            )}
                          />
                        </div>
                      </Tippy>
                    ) : (
                      <div
                        className={`flex justify-center items-center w-12 mr-1 ${getChildSvgClass(
                          CLIENT_SSO_IMAGE_GENERATOR_PATH
                        )}`}
                      >
                        <ContentIcon
                          fill={getSvgColorNews(
                            CLIENT_SSO_IMAGE_GENERATOR_PATH
                          )}
                        />
                      </div>
                    )}
                    <span
                      className={`transition-all duration-500 ease-in-out ${
                        isCollapsed
                          ? "opacity-0 max-w-0"
                          : "opacity-100 max-w-full"
                      }`}
                      style={{ whiteSpace: "nowrap", overflow: "hidden" }}
                    >
                      Image Generator
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to={CLIENT_SSO_COLLECTION_BUILD_PATH}
                    onClick={event => {
                      // Already on CB (with or without ?cbSession=): force a new empty
                      // draft. Plain <Link> to the same pathname often no-ops or leaves
                      // form + draft id, so autosave POSTs 2–3 sessions on refill.
                      if (!onCollectionPage) {
                        return;
                      }
                      event.preventDefault();
                      if (collectionJobs?.startNewDraft) {
                        collectionJobs.startNewDraft();
                        return;
                      }
                      navigate(CLIENT_SSO_COLLECTION_BUILD_PATH, {
                        replace: true,
                      });
                    }}
                    className={`flex items-center px-2 py-2 rounded-xl mb-2 ${isChildActive(
                      CLIENT_SSO_COLLECTION_BUILD_PATH
                    )}`}
                  >
                    {isCollapsed ? (
                      <Tippy content="Collection Build" placement="right">
                        <div
                          className={`flex justify-center items-center w-12 ${getChildSvgClass(
                            CLIENT_SSO_COLLECTION_BUILD_PATH
                          )}`}
                        >
                          <TextGeneratorIcon
                            fill={getSvgColorNews(
                              CLIENT_SSO_COLLECTION_BUILD_PATH
                            )}
                          />
                        </div>
                      </Tippy>
                    ) : (
                      <div
                        className={`flex justify-center items-center w-12 mr-1 ${getChildSvgClass(
                          CLIENT_SSO_COLLECTION_BUILD_PATH
                        )}`}
                      >
                        <TextGeneratorIcon
                          fill={getSvgColorNews(
                            CLIENT_SSO_COLLECTION_BUILD_PATH
                          )}
                        />
                      </div>
                    )}
                    <span
                      className={`transition-all duration-500 ease-in-out ${
                        isCollapsed
                          ? "opacity-0 max-w-0"
                          : "opacity-100 max-w-full"
                      }`}
                      style={{ whiteSpace: "nowrap", overflow: "hidden" }}
                    >
                      Collection Build
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to={CLIENT_SSO_LIBRARY_PATH}
                    state={{
                      librarySource: onCollectionPage
                        ? "collection-build"
                        : onImageGeneratorPage
                          ? "image-generator"
                          : undefined,
                    }}
                    className={`flex items-center px-2 py-2 rounded-xl mb-2 ${isChildActive(
                      CLIENT_SSO_LIBRARY_PATH
                    )}`}
                  >
                    {isCollapsed ? (
                      <Tippy content="Library" placement="right">
                        <div
                          className={`flex justify-center items-center w-12 ${getChildSvgClass(
                            CLIENT_SSO_LIBRARY_PATH
                          )}`}
                        >
                          <BoardsIcon
                            fill={getSvgColorNews(CLIENT_SSO_LIBRARY_PATH)}
                          />
                        </div>
                      </Tippy>
                    ) : (
                      <div
                        className={`flex justify-center items-center w-12 mr-1 ${getChildSvgClass(
                          CLIENT_SSO_LIBRARY_PATH
                        )}`}
                      >
                        <BoardsIcon
                          fill={getSvgColorNews(CLIENT_SSO_LIBRARY_PATH)}
                        />
                      </div>
                    )}
                    <span
                      className={`transition-all duration-500 ease-in-out ${
                        isCollapsed
                          ? "opacity-0 max-w-0"
                          : "opacity-100 max-w-full"
                      }`}
                      style={{ whiteSpace: "nowrap", overflow: "hidden" }}
                    >
                      Library
                    </span>
                  </Link>
                </li>
              </ul>
              {!isCollapsed && onCollectionPage && collectionJobs && (
                <CollectionBuildRecents
                  items={recentsItems}
                  activeKey={recentsActiveKey}
                  collapsed={!isRecentsOpen}
                  onToggle={() => setRecentsOpen(open => !open)}
                  onSelect={item => {
                    collectionJobs.openSession(item.sessionId || item.id);
                  }}
                />
              )}
              {!isCollapsed && onImageGeneratorPage && imageJobs && (
                <ImageGeneratorRecents
                  items={recentsItems}
                  activeKey={recentsActiveKey}
                  collapsed={!isRecentsOpen}
                  onToggle={() => setRecentsOpen(open => !open)}
                  onSelect={item => {
                    imageJobs.openSession(item.id);
                  }}
                />
              )}
            </div>
          </ul>
        </nav>
      </div>
      <div className="px-1.5">
        <div
          className={`flex items-center text-secondary hover:bg-gray rounded-2xl mb-2 cursor-pointer ${
            isCollapsed
              ? "p-2 bg-primary hover:bg-primary justify-center"
              : "px-4 py-2"
          }`}
          onClick={onToggle}
        >
          <CollapseIcon fill={isCollapsed ? "#ffffff" : "#4d4d4d"} />
          <span
            className={`transition-all duration-500 ease-in-out ${
              isCollapsed
                ? "opacity-0 max-w-0 ml-0"
                : "opacity-100 max-w-full ml-4"
            }`}
            style={{ whiteSpace: "nowrap", overflow: "hidden" }}
          >
            Collapse
          </span>
        </div>
      </div>
    </div>
  );
};

export default ClientSsoSidebar;
